import {
  EAiStreamerAvatarService,
  EAiStreamerJobStatus,
  EStreamerType
} from '@/api/schema';

export const STANDARD_TIMEZONES = [
  { text: '(UTC+12:00) Auckland', value: 'Pacific/Auckland' },
  { text: '(UTC+11:00) New Caledonia', value: 'Pacific/Noumea' },
  { text: '(UTC+10:00) Sydney', value: 'Australia/Sydney' },
  { text: '(UTC+09:00) Tokyo', value: 'Asia/Tokyo' },
  { text: '(UTC+08:00) Hong Kong', value: 'Asia/Hong_Kong' },
  { text: '(UTC+07:00) Bangkok', value: 'Asia/Bangkok' },
  { text: '(UTC+06:00) Bangladesh', value: 'Asia/Dhaka' },
  { text: '(UTC+05:00) Almaty', value: 'Asia/Almaty' },
  { text: '(UTC+04:00) Dubai', value: 'Asia/Dubai' },
  { text: '(UTC+03:00) Moscow', value: 'Europe/Moscow' },
  { text: '(UTC+02:00) Kyiv', value: 'Europe/Kyiv' },
  { text: '(UTC+01:00) Warsaw', value: 'Europe/Warsaw' },
  { text: '(UTC+00:00) London, Dublin', value: 'Europe/London' },
  { text: '(UTC+00:00) UTC', value: 'UTC' },
  { text: '(UTC-03:00) Sao Paulo', value: 'America/Sao_Paulo' },
  { text: '(UTC-04:00) Atlantic Time', value: 'America/Halifax' },
  { text: '(UTC-05:00) Eastern Time', value: 'America/New_York' },
  { text: '(UTC-06:00) Central Time', value: 'America/Chicago' },
  { text: '(UTC-07:00) Mountain Time', value: 'America/Denver' },
  { text: '(UTC-08:00) Pacific Time', value: 'America/Los_Angeles' },
  { text: '(UTC-09:00) Alaska', value: 'America/Anchorage' },
  { text: '(UTC-10:00) Hawaii', value: 'Pacific/Honolulu' }
];

export const DAYLIGHT_TIMEZONES = [
  { text: '(UTC+13:00) Auckland', value: 'Pacific/Auckland' },
  { text: '(UTC+11:00) New Caledonia', value: 'Pacific/Noumea' },
  { text: '(UTC+11:00) Sydney', value: 'Australia/Sydney' },
  { text: '(UTC+09:00) Tokyo', value: 'Asia/Tokyo' },
  { text: '(UTC+08:00) Hong Kong', value: 'Asia/Hong_Kong' },
  { text: '(UTC+07:00) Bangkok', value: 'Asia/Bangkok' },
  { text: '(UTC+06:00) Bangladesh', value: 'Asia/Dhaka' },
  { text: '(UTC+05:00) Almaty', value: 'Asia/Almaty' },
  { text: '(UTC+04:00) Dubai', value: 'Asia/Dubai' },
  { text: '(UTC+03:00) Moscow', value: 'Europe/Moscow' },
  { text: '(UTC+03:00) Kyiv', value: 'Europe/Kyiv' },
  { text: '(UTC+02:00) Warsaw', value: 'Europe/Warsaw' },
  { text: '(UTC+01:00) London, Dublin', value: 'Europe/London' },
  { text: '(UTC+00:00) UTC', value: 'UTC' },
  { text: '(UTC-03:00) Sao Paulo', value: 'America/Sao_Paulo' },
  { text: '(UTC-03:00) Atlantic Time', value: 'America/Halifax' },
  { text: '(UTC-04:00) Eastern Time', value: 'America/New_York' },
  { text: '(UTC-05:00) Central Time', value: 'America/Chicago' },
  { text: '(UTC-06:00) Mountain Time', value: 'America/Denver' },
  { text: '(UTC-07:00) Pacific Time', value: 'America/Los_Angeles' },
  { text: '(UTC-08:00) Alaska', value: 'America/Anchorage' },
  { text: '(UTC-10:00) Hawaii', value: 'Pacific/Honolulu' }
];

function isDaylightSavingTime(date: Date): boolean {
  const january = new Date(date.getFullYear(), 0, 1);
  const july = new Date(date.getFullYear(), 6, 1);
  const standardTimezoneOffset = Math.max(
    january.getTimezoneOffset(),
    july.getTimezoneOffset()
  );

  // Если текущее смещение меньше стандартного, значит, мы в периоде летнего времени.
  return date.getTimezoneOffset() < standardTimezoneOffset;
}

export function getSeasonalTimezones(): Record<string, string>[] {
  const today = new Date();

  if (isDaylightSavingTime(today)) {
    return DAYLIGHT_TIMEZONES;
  } else {
    return STANDARD_TIMEZONES;
  }
}

export const TIMEZONES = getSeasonalTimezones();

export enum EAiStreamerActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export const aiStreamerJobStatusMap = {
  [EAiStreamerJobStatus.STARTING]: {
    label: 'Starting',
    type: 'additional-starting'
  },
  [EAiStreamerJobStatus.RUNNING]: {
    label: 'Running',
    type: 'additional-running'
  },
  [EAiStreamerJobStatus.STOPPING]: {
    label: 'Stopping',
    type: 'additional-stopping'
  },
  [EAiStreamerJobStatus.UNRESPONSIVE]: {
    label: 'Unresponsive',
    type: 'additional-unresponsive'
  },
  [EAiStreamerJobStatus.COMPLETED]: {
    label: 'Completed',
    type: 'additional-completed'
  },
  [EAiStreamerJobStatus.FAILED]: {
    label: 'Failed',
    type: 'additional-failed'
  },
  [EAiStreamerJobStatus.STOPPED]: {
    label: 'Stopped',
    type: 'additional-finished'
  }
};

export const CAN_STOP_JOB_STATUSES = [
  EAiStreamerJobStatus.STARTING,
  EAiStreamerJobStatus.RUNNING
];

export const CAN_FORCE_STOP_JOB_STATUSES = [
  ...CAN_STOP_JOB_STATUSES,
  EAiStreamerJobStatus.STOPPING
];

export type TAiStreamerPayload = {
  supportedGameIds: number[];
  startTime: string | null;
  endTime: string | null;
  zone: string | null;
  streamerId: number | string;
  defaultBetAmount: string | number;
  currency: string;
  scenarioTemplateId: number | string;
  avatarId: string | null;
  voiceId: string | null;
  voiceLanguage: string | null;
  avatarService: EAiStreamerAvatarService;
  baseScriptId: string | null;
  startNow: boolean;
  minBalanceAmount: string | number;
};

export type TAiStreamer = {
  id: number | string;
  nickname: string;
  supportedGameIds: number[];
  startTime: string | null;
  endTime: string | null;
  zone: string;
  login: string;
  password: string;
  operatorUserId: string;
  streamerId: number | string;
  operatorId: number;
  status: EAiStreamerStatus;
  defaultBetAmount: string | number;
  currency: string;
  scenarioTemplateId: number | string;
  createdBy: number;
  updatedBy: number;
  country?: string | null;
  countryId?: number | string | null;
  avatarId: string | null;
  voiceId: string | null;
  avatarService: EAiStreamerAvatarService | null;
  voiceLanguage: string | null;
  baseScriptId: string | null;
  startNow?: boolean;
  minBalanceAmount: number | null;
  streamLinkId: string | null;
};

export type TAiStreamerListItem = {
  id: number | string;
  nickname: string;
  dateAdded: string;
  startTime: string | null;
  endTime: string | null;
  language: string;
  zone: string;
  operatorUserId: string;
  operatorId: number;
  status: EAiStreamerStatus;
  image: string;
  supportedGameIds: number[];
  streamerId: number | string;
  defaultBetAmount: string | number;
  currency: string;
  scenarioTemplateId: number | string;
  country?: string | null;
  countryId?: number | string | null;
  avatarId: string | null;
  voiceId: string | null;
  avatarService: EAiStreamerAvatarService;
  voiceLanguage: string | null;
  baseScriptId: string | null;
  activeJobId: number | string;
  streamerType: EStreamerType;
  minBalanceAmount: string | number | null;
  streamLinkId: string | null;
};

export type TAiStreamerFilter = {
  nickname: string;
  operatorUserId: string;
};

export enum EAiStreamerStatus {
  LIVE = 'LIVE',
  OFFLINE = 'OFFLINE',
  SCHEDULED = 'SCHEDULED',
  STARTING = 'STARTING'
}
